import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import _first from 'lodash/first';
import _last from 'lodash/last';

import CobaltDatePicker from '@cobalt-marketplace/react-datepicker';
import FakeInput from '@cobalt/react-fake-input';
import { useTheme } from '@cobalt/react-theme-provider';
import usePosition from '@cobalt/react-position-hook';
import useOutsideClick from '@cobalt/react-outside-click-hook';
import Flex from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import Icon from '@cobalt/react-icon';
import Input from '@cobalt/react-input';
import Popup from '@cobalt/react-popup';

import { DateType, DayType, MonthType } from './types';

const POPUP_MAX_HEIGHT = 315;

export interface DatePickerProps {
  value: string;
  years?: string[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  transparent?: boolean;
  size?: 'small' | 'medium';
  variation?: 'error' | 'warning';
  onChange: (value: string) => void;
  onBlur?: () => void;
}

type FakeInputStyledProps = { shouldFocus?: boolean };
const BaseFakeInput = styled(FakeInput)<FakeInputStyledProps>`
  &[class*='_co-input--error'] {
    border-color: ${(props) => (props.shouldFocus ? 'var(--input-focus-border-color)' : 'var(--input-border-color)')};
  }
`;
const NormalFakeInput = styled(BaseFakeInput)<FakeInputStyledProps>`
  &:not([disabled]):not([data-co-disabled]):not(&:focus):not([data-co-focus]):not(&:focus-within):not([data-co-focus-within]):not([class*='_co-input--error']) {
    border-color: ${(props) => (props.shouldFocus ? 'var(--input-focus-border-color)' : 'var(--input-border-color)')};

    &[data-co-hover],
    &:hover {
      border-color: ${(props) =>
        props.shouldFocus ? 'var(--input-focus-border-color)' : 'var(--input-hover-border-color)'};
    }
  }
`;
const TransparentFakeInput = styled(BaseFakeInput)<FakeInputStyledProps>`
  transition-property: border, background;

  &:not([disabled]):not([data-co-disabled]):not(&:focus):not([data-co-focus]):not(&:focus-within):not([data-co-focus-within]):not([class*='_co-input--error']) {
    border-color: ${(props) => (props.shouldFocus ? 'var(--input-focus-border-color)' : 'transparent')};

    &[data-co-hover],
    &:hover {
      background-color: ${(props) => (props.shouldFocus ? 'transparent' : 'var(--gray-200)')};
    }

    &:not([data-co-hover]):not(&:hover) {
      .clear-icon {
        display: none;
      }
    }
  }

  &[disabled],
  &[data-co-disabled] {
    border-color: transparent;
    background-color: transparent;
  }
`;

const DatePicker = (props: DatePickerProps) => {
  const [t] = useTranslation();

  const {
    value,
    years,
    placeholder = t('common.none', { defaultValue: 'None' }),
    disabled,
    allowClear,
    transparent,
    size,
    variation,
    onChange,
    onBlur,
  } = props;

  const theme = useTheme();

  const [visible, setVisible] = useState(false);

  const fakeInputRef = useRef<HTMLDivElement>(null);

  const iconSize = size === 'small' ? 'micro' : 'tiny';

  const availableYears = useMemo(() => {
    return years
      ? years
      : Array(11)
          .fill(new Date().getFullYear() - 2)
          .map((v, i) => v + i);
  }, [years]);

  const disabledDates = useMemo(() => {
    const firstPrevYear = Number(_first(availableYears)) - 1;
    const lastNextYear = Number(_last(availableYears)) + 1;

    return [
      { start: { year: firstPrevYear, month: 12, day: 26 }, end: { year: firstPrevYear, month: 12, day: 31 } },
      { start: { year: lastNextYear, month: 1, day: 1 }, end: { year: lastNextYear, month: 1, day: 6 } },
    ];
  }, [availableYears]);

  const dayValue = useMemo(() => {
    if (!value) return;
    return dayjs(value);
  }, [value]);

  const direction = (() => {
    const { clientHeight } = document.body;
    const bottom = fakeInputRef.current?.getBoundingClientRect().bottom || 0;
    const offsetBottom = clientHeight - bottom;
    const contentHeight = POPUP_MAX_HEIGHT;
    return offsetBottom > contentHeight ? 'down' : 'up';
  })();
  const { position } = usePosition(fakeInputRef.current, {
    origin: direction === 'down' ? 'SW' : 'NW',
    direction: direction === 'down' ? 'SE' : 'NE',
    autoGap: 4,
  });

  const outsideClickHandler = useCallback(() => setVisible(false), []);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const genDateString = (year: string | number, month: MonthType | number, day: DayType) => {
    const tempMonth = `-${month.toString().padStart(2, '0')}`;
    const tempDay = `-${day.toString().padStart(2, '0')}`;
    return `${year}${tempMonth}${tempDay}`;
  };

  const handleClickInput = () => {
    if (disabled) return;
    onClickCapture();
    setVisible(!visible);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
    onChange('');
  };

  const handleClickDay = ({ year, month, day }: DateType) => {
    onChange(genDateString(year, month, day as DayType));
    setVisible(false);
  };

  const FakeInputC = !transparent ? NormalFakeInput : TransparentFakeInput;

  return (
    <>
      <FakeInputC
        forwardedRef={fakeInputRef}
        disabled={disabled}
        shouldFocus={visible}
        size={size}
        variation={variation}
        onClick={handleClickInput}
        onBlur={onBlur}
      >
        <Flex alignY="center" height="100%">
          {/* Calendar icon */}
          <Icon name="calendar_today" size={iconSize} />

          {/* Date input */}
          <Box style={{ flex: 1, overflowX: 'hidden' }} paddingX="2" height="100%">
            <Input
              value={value}
              variation="unstyled"
              size={size}
              disabled={disabled}
              placeholder={placeholder}
              onChange={() => {}}
            />
          </Box>

          {/* Clear icon */}
          <Flex className="clear-icon" alignX="end" alignY="center">
            {!disabled && allowClear && value && (
              <Box onClick={handleClear}>
                <Icon
                  style={{ display: 'block', cursor: 'pointer' }}
                  name="clear"
                  size={iconSize}
                  color={theme.gray600}
                />
              </Box>
            )}
          </Flex>
        </Flex>
      </FakeInputC>

      <Popup visible={visible} style={{ ...position, zIndex: 1 }} onClick={onClickCapture}>
        <Box style={{ lineHeight: '1.25rem' }}>
          <CobaltDatePicker
            selectedDay={
              dayValue ? { year: dayValue.year(), month: dayValue.month() + 1, day: dayValue.date() } : undefined
            }
            availableYears={availableYears}
            disabledDays={disabledDates}
            withNavigation
            highlightToday
            onDayClick={handleClickDay}
          />
        </Box>
      </Popup>
    </>
  );
};

export default DatePicker;
